import React from 'react'
import { Marquee } from '@devnomic/marquee'
import classNames from 'classnames'
import LazyHydrate from 'react-lazy-hydration'
import { FlexColumn } from '@/atoms/FlexContainers'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsH1, AsSpan, HeadingSM, ParagraphSM } from '@/atoms/Text'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useTranslate } from '@/utils/translate/translate-client'
import useWindowSize from '@/utils/useWindowSize'
import { HeroImages } from '../types'

interface HeroSectionProps {
  heroImages: HeroImages
}

export const HeroSection: React.FC<HeroSectionProps> = ({ heroImages }) => {
  const { t } = useTranslate('music')
  const { innerWidth, innerHeight } = useWindowSize()
  const imageHeight = React.useMemo(() => {
    return innerWidth > 2000
      ? 300
      : innerWidth > 1300
      ? 200
      : innerWidth > 768
      ? 170
      : innerWidth > 476
      ? 170
      : innerHeight > 670
      ? 110
      : 100
  }, [innerWidth, innerHeight])
  const imageWidth = React.useMemo(() => {
    return innerWidth > 2000
      ? 300
      : innerWidth > 1300
      ? 200
      : innerWidth > 768
      ? 170
      : innerWidth > 476
      ? 170
      : innerHeight > 670
      ? 110
      : 100
  }, [innerWidth, innerHeight])

  const firstColumn = heroImages?.firstColumn ?? []
  const secondColumn = heroImages?.secondColumn ?? []
  const thirdColumn = heroImages?.thirdColumn ?? []

  return (
    <section
      aria-labelledby="music-heading"
      className="2xl:max-h-auto relative h-full min-h-screen w-full overflow-hidden"
    >
      <ResponsiveBackgroundImage
        src={
          'https://images.angelstudios.com/image/upload/w_3440,f_webp,q_auto,ar_16:9,c_fill/q_auto:low/v1732031732/angel-studios/guild/hero/Hero-Desk-Image-min.webp'
        }
        className="pointer-events-none touch-none select-none"
        transforms="q_auto:low"
      />
      <div
        className={classNames(
          'h-screen min-h-screen absolute left-0 top-0 z-10 w-full bg-transparent px-4 md:max-h-[800px] md:px-8 xl:mx-[40px] xl:px-4',
        )}
      >
        <div className="relative flex h-full w-full flex-col-reverse items-center justify-center lg:!flex-row 2xl:mx-auto 2xl:max-w-[1600px]">
          <div className="-mt-auto flex h-full w-full flex-col items-start justify-start gap-y-4 bg-transparent py-0 opacity-100 md:items-center md:gap-y-6 lg:-mt-40 lg:w-1/2 lg:items-start lg:justify-center lg:py-32 2xl:pl-10">
            <header className="mx-auto max-w-[320px] sm:max-w-[475px] md:max-w-[650px]">
              <FlexColumn className="gap-8">
                <AngelLogo className="mx-auto md:hidden" color="white" height={80} />
                <AngelLogo className="mx-auto hidden md:block" color="white" height={110} />
                <HeadingSM as={AsH1} id="music-heading" className="md:photon-heading-lg text-center" weight="bold">
                  {t('angelStudiosMusicTitle', 'The Music of Angel')}
                </HeadingSM>
                <ParagraphSM
                  as={AsSpan}
                  className="md:photon-paragraph-md lg:photon-paragraph-lg mb-3 w-full text-balance bg-transparent text-center"
                  color="core-gray-400"
                >
                  {t(
                    'experienceOriginalMusicAndScores',
                    'Experience original music and scores from your favorite Angel Films and Series! At Angel, we truly value how audiences connect with the power of original music to visual media.',
                  )}
                </ParagraphSM>
              </FlexColumn>
            </header>
          </div>
          <div
            className={classNames(
              'z-10 mb-[45px] flex h-1/2 w-full justify-center gap-x-4 opacity-70 lg:my-0 lg:h-full lg:w-1/2 lg:justify-center lg:opacity-100',
            )}
          >
            <link
              rel="preload"
              href="https://images.angelstudios.com/image/upload/v1732132444/angel-studios/guild/hero/css_sprites_5.webp"
              as="image"
            ></link>
            <LazyHydrate whenVisible>
              <>
                {innerWidth && innerHeight && (
                  <Marquee
                    direction="up"
                    reverse
                    className="fade-marquee h-full w-1/3 animate-fadein md:w-auto"
                    innerClassName="items-center"
                  >
                    {firstColumn?.map((poster) => {
                      const imgSrc = getCloudinaryImageUrl({ path: poster.imageUrl, width: imageWidth })
                      const background = `url('${imgSrc}')`
                      return (
                        <div
                          key={JSON.stringify({ poster })}
                          style={{
                            height: imageHeight,
                            width: imageWidth,
                          }}
                          className="rounded-2xl"
                        >
                          <div
                            style={{
                              background,
                            }}
                            className="pointer-events-none h-full w-full max-w-full touch-none select-none rounded-2xl bg-center"
                          />
                        </div>
                      )
                    })}
                  </Marquee>
                )}
              </>
            </LazyHydrate>
            <LazyHydrate whenVisible>
              <>
                {innerWidth && innerHeight && (
                  <Marquee
                    direction="up"
                    className="fade-marquee h-full w-1/3 animate-fadein [--duration:32s] md:w-auto"
                    innerClassName="[--duration:32s] items-center"
                  >
                    {secondColumn?.map((poster) => {
                      const imgSrc = getCloudinaryImageUrl({ path: poster.imageUrl, width: imageWidth })
                      const background = `url('${imgSrc}')`
                      return (
                        <div
                          key={JSON.stringify({ poster })}
                          style={{
                            height: imageHeight,
                            width: imageWidth,
                          }}
                          className="rounded-2xl"
                        >
                          <div
                            style={{
                              background,
                            }}
                            className="pointer-events-none h-full w-full max-w-full touch-none select-none rounded-2xl bg-center "
                          />
                        </div>
                      )
                    })}
                  </Marquee>
                )}
              </>
            </LazyHydrate>
            <LazyHydrate whenVisible>
              <>
                {innerWidth && innerHeight && (
                  <Marquee
                    direction="up"
                    reverse
                    className="fade-marquee w-1/3 animate-fadein md:w-auto"
                    innerClassName="items-center"
                  >
                    {thirdColumn?.map((poster) => {
                      const imgSrc = getCloudinaryImageUrl({ path: poster.imageUrl, width: imageWidth })
                      const background = `url('${imgSrc}')`
                      return (
                        <div
                          key={JSON.stringify({ poster })}
                          style={{
                            height: imageHeight,
                            width: imageWidth,
                          }}
                          className="rounded-2xl"
                        >
                          <div
                            style={{
                              background,
                            }}
                            className="pointer-events-none h-full w-full max-w-full touch-none select-none rounded-2xl bg-center"
                          />
                        </div>
                      )
                    })}
                  </Marquee>
                )}
              </>
            </LazyHydrate>
          </div>
        </div>
      </div>
    </section>
  )
}
